import {sanitizeStringFromDashes, type Participant, type ShortGuest} from '@chekin/core';

const normalize = (value?: string | null) =>
  (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

export function getDuplicateIdentity(participant: Participant): string {
  return [
    normalize(participant.name),
    normalize(participant.surname),
    normalize(participant.birth_date),
    normalize(participant.document_number),
  ].join('|');
}

function getNameAndDocumentIdentity(fullName: string, documentNumber?: string | null) {
  return `${normalize(fullName)}|${normalize(documentNumber)}`;
}

export function getParticipantNameAndDocumentIdentity(participant: Participant) {
  return getNameAndDocumentIdentity(
    `${participant.name} ${participant.surname}`,
    participant.document_number,
  );
}

function getNameAndBirthDateIdentity(fullName: string, birthDate?: string | null) {
  return `${normalize(fullName)}|${normalize(birthDate)}`;
}

export function getParticipantNameAndBirthDateIdentity(participant: Participant) {
  return getNameAndBirthDateIdentity(
    `${participant.name} ${participant.surname}`,
    participant.birth_date,
  );
}

export function getRegisteredIdentities(
  guests: ShortGuest[] = [],
  participants: Participant[] = [],
) {
  const participantsById = new Map(
    participants.map(participant => [
      sanitizeStringFromDashes(participant.id),
      participant,
    ]),
  );

  const exact = new Set<string>();
  const nameAndDocument = new Set<string>();
  const nameAndBirthDate = new Set<string>();

  const addIfComplete = (set: Set<string>, key: string) => {
    if (key.endsWith('|')) return;
    set.add(key);
  };

  guests.forEach(guest => {
    const linked = guest.participant_id
      ? participantsById.get(sanitizeStringFromDashes(guest.participant_id))
      : undefined;

    if (linked) {
      exact.add(getDuplicateIdentity(linked));
      addIfComplete(nameAndDocument, getParticipantNameAndDocumentIdentity(linked));
      addIfComplete(nameAndBirthDate, getParticipantNameAndBirthDateIdentity(linked));
    }

    addIfComplete(
      nameAndDocument,
      getNameAndDocumentIdentity(guest.full_name, guest.document_number),
    );
  });

  return {exact, nameAndDocument, nameAndBirthDate};
}

export function getConflictKeys(participant: Participant): string[] {
  const nameAndDocument = getParticipantNameAndDocumentIdentity(participant);
  const nameAndBirthDate = getParticipantNameAndBirthDateIdentity(participant);

  return [
    `exact:${getDuplicateIdentity(participant)}`,
    ...(nameAndDocument.endsWith('|') ? [] : [`document:${nameAndDocument}`]),
    ...(nameAndBirthDate.endsWith('|') ? [] : [`birth:${nameAndBirthDate}`]),
  ];
}

export function pickDistinctParticipants(participants: Participant[]): Participant[] {
  const seen = new Set<string>();

  return participants.filter(participant => {
    const keys = getConflictKeys(participant);

    if (keys.some(key => seen.has(key))) return false;
    keys.forEach(key => seen.add(key));

    return true;
  });
}
